import { i18n } from "@/shared/config/i18n/vue-i18n";

export const useDateFormatter = () => {
  /**
   * Formats a full date to the API standard format: "YYYY-MM-DD"
   * @param date A valid Date object
   * @returns A string formatted as "YYYY-MM-DD"
   */
  const formatDateApi = (date: Date): string => {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  /**
   * Formats a date to the API month format: "YYYYMM"
   * @param date A valid Date object
   * @returns A string formatted as "YYYYMM"
   */
  const formatMonthDateApi = (date: Date): string => {
    return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}`;
  };

  /**
   * Formats a full date with time to the API standard format: "YYYY-MM-DDTHH:mm:ss"
   * @param date A valid Date object
   * @returns A string formatted as "YYYY-MM-DDTHH:mm:ss"
   */
  const formatDateTimeApi = (date: Date): string => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}T${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
  };

  /**
   * Converts a string in "YYYY-MM-DD" format to a localized date string ("DD/MM/YYYY")
   * using the "pt-BR" locale.
   * @param date A string formatted as "YYYY-MM-DD"
   * @returns A localized date string or undefined if invalid
   */
  const stringToDate = (date?: string): string | undefined => {
    if (!date) return;

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-").map(Number);
      if (!year || !month || !day) return;
      const parsedDate = new Date(year, month - 1, day);
      return isNaN(parsedDate.getTime()) ? undefined : parsedDate.toLocaleDateString("pt-BR");
    }

    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return;

    const d = String(parsedDate.getDate()).padStart(2, "0");
    const m = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const y = parsedDate.getFullYear();

    return `${d}/${m}/${y}`;
  };

  /**
   * Converts a string in "YYYYMM" format to a "MM/YYYY" string
   * @param date A string formatted as "YYYYMM"
   * @returns A string formatted as "MM/YYYY" or undefined if invalid
   */
  const stringToMonthDate = (date?: string): string | undefined => {
    if (!date || date.length < 6) return;

    const year: string = date.slice(0, 4);
    const month: string = date.slice(4, 6);

    if (isNaN(Number(year)) || isNaN(Number(month))) return;

    return `${month}/${year}`;
  };

  /**
   * Converts a string in "YYYY-MM-DDTHH:mm:ss" format to a localized date-time string ("DD/MM/YYYY HH:mm:ss")
   * using the "pt-BR" locale.
   * @param dateTime A string formatted as "YYYY-MM-DDTHH:mm:ss"
   * @returns A localized date-time string or undefined if invalid
   */
  const stringToDateTime = (dateTime?: string): string | undefined => {
    if (!dateTime) return;

    const parsedDate = new Date(dateTime);

    if (isNaN(parsedDate.getTime())) return;

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const seconds = String(parsedDate.getSeconds()).padStart(2, "0");

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  /**
   * Converts a string in "YYYY-MM-DDTHH:mm:ss" format to a localized time string ("HH:mm")
   * @param dateTime A string formatted as "YYYY-MM-DDTHH:mm:ss"
   * @returns A localized time string or undefined if invalid
   */
  const stringToTime = (dateTime?: string): string | undefined => {
    if (!dateTime) return;

    const parsedDate = new Date(dateTime);

    if (isNaN(parsedDate.getTime())) return;

    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");

    return `${hours}:${minutes}`;
  };

  /**
   * Converts a string in "YYYY-MM-DDTHH:mm:ss" format to a localized time string with seconds ("HH:mm:ss")
   * @param dateTime A string formatted as "YYYY-MM-DDTHH:mm:ss"
   * @returns A localized time string or undefined if invalid
   */
  const stringToTimeWithSeconds = (dateTime?: string): string | undefined => {
    if (!dateTime) return;

    const parsedDate = new Date(dateTime);

    if (isNaN(parsedDate.getTime())) return;

    const hours = String(parsedDate.getHours()).padStart(2, "0");
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const seconds = String(parsedDate.getSeconds()).padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };

  /**
   * Returns a short weekday string (e.g. "Seg", "Ter") from a date string
   * @param dateTime A valid date string
   * @returns A short weekday string
   */
  const stringToShortWeekday = (date?: string): string | undefined => {
    if (!date) return undefined;
    const d = new Date(date);
    const weekdays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    return weekdays[d.getDay()];
  };

  const stringToFormattedDate = (date?: string): string | undefined => {
    if (!date) return undefined;
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, "0");
    const months = [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ];
    const month = months[d.getMonth()];
    return `${day} de ${month}`;
  };

  /**
   * Returns a relative time string translated via i18n
   * @param dateTime A valid date string
   * @returns A relative time string
   */
  const stringToRelativeTime = (dateTime?: string): string | undefined => {
    if (!dateTime) return undefined;

    const parsedDate = new Date(dateTime);
    if (isNaN(parsedDate.getTime())) return undefined;

    const diffInSeconds = Math.max(
      0,
      Math.floor((new Date().getTime() - parsedDate.getTime()) / 1000),
    );

    if (diffInSeconds < 60) {
      return i18n.global.t("just_now");
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return i18n.global.t("minutes_ago", diffInMinutes);
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return i18n.global.t("hours_ago", diffInHours);
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) {
      return i18n.global.t("days_ago", diffInDays);
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return i18n.global.t("months_ago", diffInMonths);
    }

    const diffInYears = Math.floor(diffInMonths / 12);
    return i18n.global.t("years_ago", diffInYears);
  };

  return {
    formatDateApi,
    formatMonthDateApi,
    stringToDate,
    stringToMonthDate,
    formatDateTimeApi,
    stringToDateTime,
    stringToTime,
    stringToTimeWithSeconds,
    stringToShortWeekday,
    stringToFormattedDate,
    stringToRelativeTime,
  };
};
